import { useMemo, useState } from "react";
import cx from "classnames";
import * as Select from "@radix-ui/react-select";

import ChevronDown from "resources/icons/chevron-down.svg";
import Checkmark from "resources/icons/checkmark.svg";
import Loading from "resources/icons/loading.svg";

import styles from "./Dropdown.module.scss";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
}

interface SelectProps {
  "aria-label"?: React.ComponentProps<"button">["aria-label"];
  value?: string;
  prefix?: React.ReactNode;
  className?: string;
  options: SelectOption[];
  isLoading?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  hideSelectedFromOptions?: boolean;
  onChange?: (value: string) => void;
}

export function Dropdown({
  "aria-label": ariaLabel,
  value,
  prefix,
  options = [],
  placeholder,
  hideSelectedFromOptions = false,
  isLoading = false,
  isDisabled = false,
  className,
  onChange,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [pressedItem, setPressedItem] = useState<string | null>(null);

  const selectedItem = options.find((option) => option.value === value);

  const filteredOptions = useMemo(() => {
    if (!hideSelectedFromOptions || !value) {
      return options;
    }
    return options.filter((option) => option.value !== value);
  }, [options, value, hideSelectedFromOptions]);

  const triggerClasses = cx(
    styles.trigger,
    {
      [styles.triggerOpen]: isOpen && !isDisabled && !isLoading,
      [styles.triggerHover]: isHovering && !isDisabled && !isLoading,
      [styles.triggerPressed]: isPressing && !isDisabled && !isLoading,
      [styles.triggerDisabled]: isDisabled,
    },
    className
  );

  return (
    <Select.Root
      value={value}
      open={isOpen}
      disabled={isDisabled || isLoading}
      onOpenChange={setIsOpen}
      onValueChange={onChange}
    >
      <Select.Trigger
        aria-label={ariaLabel}
        className={triggerClasses}
        onMouseDown={() => setIsPressing(true)}
        onMouseUp={() => setIsPressing(false)}
        onMouseLeave={() => {
          setIsPressing(false);
          setIsHovering(false);
        }}
        onMouseEnter={() => setIsHovering(true)}
      >
        <span className={styles.content}>
          {prefix && <span className={styles.prefix}>{prefix}</span>}
          <span className={styles.value}>
            {selectedItem ? selectedItem.label : placeholder}
          </span>
        </span>

        {isLoading ? (
          <Loading className={styles.loading} />
        ) : (
          <Select.Icon>
            <ChevronDown
              className={cx(styles.chevron, {
                [styles.chevronRotated]: isOpen,
              })}
            />
          </Select.Icon>
        )}
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className={styles.selectContent}
          position="popper"
          sideOffset={8}
          align="start"
          collisionPadding={10}
          avoidCollisions={true}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <Select.Viewport className={styles.viewport}>
            {filteredOptions.map((option) => {
              const isSelected = selectedItem?.value === option.value;
              const isPressed = pressedItem === option.value;

              const itemClasses = cx(styles.item, {
                [styles.itemSelected]: isSelected,
                [styles.itemPressed]: isPressed,
                [styles.itemDisabled]: option.disabled,
              });

              return (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={itemClasses}
                  disabled={option.disabled}
                  onMouseDown={() => setPressedItem(option.value)}
                  onMouseUp={() => setPressedItem(null)}
                  onMouseLeave={() => setPressedItem(null)}
                >
                  <Select.ItemText asChild>
                    <span className={styles.value}>{option.label}</span>
                  </Select.ItemText>

                  {option.isLoading ? (
                    <Loading className={styles.loading} />
                  ) : (
                    <Select.ItemIndicator className={styles.indicator}>
                      <Checkmark />
                    </Select.ItemIndicator>
                  )}
                </Select.Item>
              );
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

export default Dropdown;
